import { useState, type ComponentProps, type ComponentPropsWithoutRef, type ReactNode } from "react";
import ReactMarkdown, { type Components } from "react-markdown";
import remarkGfm from "remark-gfm";
import remarkMath from "remark-math";
import rehypeHighlight from "rehype-highlight";
import rehypeKatex from "rehype-katex";
import rehypeRaw from "rehype-raw";
import rehypeSanitize from "rehype-sanitize";
import rehypeSlug from "rehype-slug";
import { codeLanguage, markdownSchema, nodeText, rehypeArticlePolish, transformUrl } from "../lib/markdown";
import { Mermaid } from "./Mermaid";
import "katex/dist/katex.min.css";
import "./markdown.css";

type NodeProps = { node?: unknown };
type HastLike = { children?: { type?: string; tagName?: string; value?: string; properties?: Record<string, unknown> }[] };

type MarkdownProps = ComponentProps<typeof ReactMarkdown>;

const remarkPlugins: MarkdownProps["remarkPlugins"] = [remarkGfm, remarkMath];
// Raw HTML is parsed, then sanitised, and only then do KaTeX and highlighting
// add their own markup — so nothing an author wrote can survive as behaviour.
const rehypePlugins: MarkdownProps["rehypePlugins"] = [
  rehypeRaw,
  rehypeSlug,
  [rehypeSanitize, markdownSchema],
  rehypeArticlePolish,
  [rehypeKatex, { throwOnError: false, strict: false }],
  [rehypeHighlight, { detect: false, ignoreMissing: true }]
];

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      type="button"
      className="md-copy"
      onClick={() => {
        void navigator.clipboard?.writeText(text).then(
          () => {
            setCopied(true);
            window.setTimeout(() => setCopied(false), 1600);
          },
          () => setCopied(false)
        );
      }}
    >
      {copied ? "Copied" : "Copy"}
    </button>
  );
}

function CodeBlock({ node, children, ...rest }: ComponentPropsWithoutRef<"pre"> & NodeProps) {
  const code = (node as HastLike | undefined)?.children?.find((child) => child.tagName === "code");
  const language = codeLanguage(code?.properties?.className);
  const source = nodeText(code);

  if (language === "mermaid") return <Mermaid code={source} />;

  return (
    <div className="md-code">
      <div className="md-code-bar">
        <span>{language || "text"}</span>
        <CopyButton text={source} />
      </div>
      <pre {...rest}>{children}</pre>
    </div>
  );
}

function Paragraph({ node, children, ...rest }: ComponentPropsWithoutRef<"p"> & NodeProps) {
  const content = ((node as HastLike | undefined)?.children ?? []).filter(
    (child) => child.type !== "text" || (child.value ?? "").trim()
  );
  const image = content.length === 1 && content[0].tagName === "img" ? content[0] : null;
  const caption = typeof image?.properties?.title === "string" ? image.properties.title : "";

  // A lone image becomes a figure, so captions and spacing behave like a block.
  if (image) {
    return (
      <figure className="md-figure">
        {children}
        {caption ? <figcaption>{caption}</figcaption> : null}
      </figure>
    );
  }
  return <p {...rest}>{children}</p>;
}

/** Headings carry the id the sanitiser produced, so sections can be linked to. */
function anchor(id?: string) {
  return id ? (
    <a className="md-anchor" href={`#${id}`} aria-label="Link to this section">
      #
    </a>
  ) : null;
}

function Heading2({ node, children, id, ...rest }: ComponentPropsWithoutRef<"h2"> & NodeProps) {
  void node;
  return <h2 id={id} {...rest}>{anchor(id)}{children}</h2>;
}

function Heading3({ node, children, id, ...rest }: ComponentPropsWithoutRef<"h3"> & NodeProps) {
  void node;
  return <h3 id={id} {...rest}>{anchor(id)}{children}</h3>;
}

function Heading4({ node, children, id, ...rest }: ComponentPropsWithoutRef<"h4"> & NodeProps) {
  void node;
  return <h4 id={id} {...rest}>{anchor(id)}{children}</h4>;
}

function Table({ node, children, ...rest }: ComponentPropsWithoutRef<"table"> & NodeProps) {
  void node;
  return (
    <div className="md-table">
      <table {...rest}>{children}</table>
    </div>
  );
}

const components: Components = {
  pre: CodeBlock,
  p: Paragraph,
  h2: Heading2,
  h3: Heading3,
  h4: Heading4,
  table: Table
};

/** Renders Markdown exactly as written: GFM, maths, diagrams, images and safe HTML. */
export function Markdown({ content, className = "markdown" }: { content: string; className?: string }): ReactNode {
  return (
    <div className={className}>
      <ReactMarkdown
        remarkPlugins={remarkPlugins}
        rehypePlugins={rehypePlugins}
        components={components}
        urlTransform={transformUrl}
      >
        {content}
      </ReactMarkdown>
    </div>
  );
}
